import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { IonModal } from "@ionic/angular";
import { RewardsService } from "../../../../../services/rewards/rewards.service";
import { LoadingService } from "../../../../../services/loading/loading.service";
import { MessageService } from "../../../../../services/message/message.service";
import { numberWithCommas } from "../../../../../services/utils/number.utils";

@Component({
  selector: "rc-rewards-section",
  templateUrl: "./rewards-section.component.html",
  styleUrls: ["./rewards-section.component.scss"],
  standalone: false,
})
export class RewardsSectionComponent implements OnInit {
  @Input() public userId: string;
  @ViewChild("transactionModal") transactionModal: IonModal;

  public balance: string = "0";
  public adjustmentAmount: number | null = null;
  public adjustmentDescription: string = "";
  public isSubmitting: boolean = false;

  constructor(
    private readonly rewardsService: RewardsService,
    private readonly loadingService: LoadingService,
    private readonly messageService: MessageService,
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.loadingService.wrapInLoading(async () => {
      await this.loadBalance();
    });
  }

  public async loadBalance(): Promise<void> {
    if (!this.userId) {
      return;
    }

    try {
      const response = await this.rewardsService.getBalanceForUser(this.userId);
      this.balance = numberWithCommas(parseInt(response.balance, 10));
    } catch (error) {
      console.error("Failed to load balance:", error);
      await this.messageService.presentErrorToast("Failed to load points balance");
      this.balance = "0";
    }
  }

  public async addPoints(): Promise<void> {
    const amount = this.adjustmentAmount;
    if (!this.validateForm() || amount === null) {
      return;
    }

    await this.loadingService.wrapInLoading(async () => {
      try {
        this.isSubmitting = true;
        await this.rewardsService.increasePoints(this.userId, amount, this.adjustmentDescription);
        await this.messageService.presentSuccessToast(`Successfully added ${numberWithCommas(amount)} points`);
        this.resetForm();
        await this.loadBalance();
      } catch (error) {
        console.error("Failed to add points:", error);
        await this.messageService.presentErrorToast("Failed to add points");
      } finally {
        this.isSubmitting = false;
      }
    });
  }

  public async subtractPoints(): Promise<void> {
    const amount = this.adjustmentAmount;
    if (!this.validateForm() || amount === null) {
      return;
    }

    await this.loadingService.wrapInLoading(async () => {
      try {
        this.isSubmitting = true;
        await this.rewardsService.decreasePoints(this.userId, amount, this.adjustmentDescription);
        await this.messageService.presentSuccessToast(`Successfully deducted ${numberWithCommas(amount)} points`);
        this.resetForm();
        await this.loadBalance();
      } catch (error) {
        console.error("Failed to subtract points:", error);
        await this.messageService.presentErrorToast("Failed to subtract points");
      } finally {
        this.isSubmitting = false;
      }
    });
  }

  public openTransactionModal(): void {
    this.transactionModal.present();
  }

  public closeTransactionModal(): void {
    this.transactionModal.dismiss();
  }

  private validateForm(): boolean {
    if (!this.adjustmentAmount || this.adjustmentAmount <= 0) {
      this.messageService.presentErrorToast("Amount must be a positive number");
      return false;
    }

    if (!this.adjustmentDescription || this.adjustmentDescription.trim().length === 0) {
      this.messageService.presentErrorToast("Description is required");
      return false;
    }

    if (this.adjustmentDescription.length > 500) {
      this.messageService.presentErrorToast("Description must be less than 500 characters");
      return false;
    }

    return true;
  }

  private resetForm(): void {
    this.adjustmentAmount = null;
    this.adjustmentDescription = "";
  }
}
